import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Inbox, Loader2, Search, Download, AlertTriangle, Mail, MessageCircle } from "lucide-react";
import { apiFetch, formatDetail, downloadCsv, DetailModal, SheetsConnectCard } from "./bits";

const PREVIEW_COLS = 4;

const findField = (row, headers, patterns) => {
  const h = headers.find((x) => patterns.some((p) => x.toLowerCase().includes(p)));
  return h ? String(row[h] || "").trim() : "";
};

const whatsappNumber = (raw) => {
  let digits = raw.replace(/\D/g, "");
  if (digits.startsWith("00")) digits = digits.slice(2);
  else if (digits.startsWith("0")) digits = `234${digits.slice(1)}`;
  return digits;
};

export const SheetRecordsView = ({ testId, endpoint, exportPath, exportFile, emptyLabel, followupNoun, refreshKey, onAuthFail }) => {
  const [data, setData] = useState(null);
  const [needsConnect, setNeedsConnect] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await apiFetch(endpoint);
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        setData(body);
        setNeedsConnect(null);
      } else if (res.status === 401) {
        onAuthFail();
      } else if (res.status === 409 || res.status === 503) {
        const st = await apiFetch("/admin/sheets/status");
        setNeedsConnect(st.ok ? await st.json() : { configured: false, connected: false });
      } else {
        setError(formatDetail(body.detail));
      }
    } catch {
      setError("Could not load records.");
    }
  }, [endpoint, onAuthFail]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (needsConnect) {
    return (
      <div className="mt-12">
        <SheetsConnectCard testId={`${testId}-connect`} status={needsConnect} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-dark mt-12 flex flex-col items-center p-16 text-center hover:transform-none" data-testid={`${testId}-error`}>
        <AlertTriangle size={28} className="text-[#1E3A8A]/60" />
        <p className="mt-4 font-sans text-xl text-[#0F172A]">Could not load records</p>
        <p className="mt-2 max-w-md text-sm text-[#94A3B8]">{error}</p>
        <button onClick={load} data-testid={`${testId}-retry`} className="btn-ghost mt-6 !px-5 !py-2 !text-xs">Try Again</button>
      </div>
    );
  }

  if (!data) {
    return <div className="mt-12 flex justify-center py-16"><Loader2 size={24} className="animate-spin text-[#1E3A8A]" /></div>;
  }

  const needle = q.trim().toLowerCase();
  const rows = needle
    ? data.rows.filter((r) => Object.values(r).join(" ").toLowerCase().includes(needle))
    : data.rows;
  const previewHeaders = data.headers.slice(0, PREVIEW_COLS);

  return (
    <div className="mt-12" data-testid={`${testId}-view`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex min-w-0 flex-1 gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid={`${testId}-search-input`}
            className="field !py-2.5 !text-sm"
            placeholder="Search any field…"
            aria-label="Search records"
          />
        </div>
        <button onClick={() => downloadCsv(exportPath, exportFile)} data-testid={`${testId}-export-button`} className="btn-ghost shrink-0 !px-5 !py-2.5 !text-xs">
          <Download size={14} /> Export CSV
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="card-dark mt-8 flex flex-col items-center p-16 text-center hover:transform-none" data-testid={`${testId}-empty`}>
          <Inbox size={28} className="text-[#1E3A8A]/60" />
          <p className="mt-4 font-sans text-xl text-[#0F172A]">{needle ? "No records match your search" : emptyLabel}</p>
          <p className="mt-2 text-sm text-[#94A3B8]">{needle ? "Try a different search term." : "New form submissions will appear here automatically."}</p>
        </div>
      ) : (
        <div className="mt-8">
          <p className="font-sans text-xs tracking-[0.25em] text-[#94A3B8]" data-testid={`${testId}-count`}>
            {rows.length} RECORD{rows.length === 1 ? "" : "S"}
          </p>
          <div className="card-dark mt-4 overflow-x-auto p-0 hover:transform-none" data-testid={`${testId}-table`}>
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead>
                <tr className="border-b border-[#E2E8F0]">
                  {previewHeaders.map((h) => (
                    <th key={h} className="px-5 py-4 font-sans text-[10px] font-normal tracking-[0.2em] text-[#94A3B8]">{h.toUpperCase()}</th>
                  ))}
                  <th className="px-5 py-4" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={i}
                    onClick={() => setSelected(r)}
                    data-testid={`${testId}-row-${i}`}
                    className="cursor-pointer border-b border-[#E2E8F0] transition-colors last:border-0 hover:bg-[#1E3A8A]/5"
                  >
                    {previewHeaders.map((h) => (
                      <td key={h} className="max-w-[220px] truncate px-5 py-4 text-[#0F172A]/65">{r[h] || "—"}</td>
                    ))}
                    <td className="px-5 py-4 text-right font-sans text-[10px] tracking-[0.2em] text-[#1E3A8A]/70">VIEW</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selected && (
        <DetailModal title="Record Details" onClose={() => setSelected(null)} testId={`${testId}-detail-modal`}>
          {(() => {
            const email = findField(selected, data.headers, ["email"]);
            const phone = findField(selected, data.headers, ["whatsapp", "phone"]);
            const name = findField(selected, data.headers, ["business", "name"]) || "there";
            const wa = phone ? whatsappNumber(phone) : "";
            const subject = encodeURIComponent("Aflimart Digital Market — Following up on your submission");
            const body = encodeURIComponent(
              `Hello ${name},\n\nThank you for the ${followupNoun} you submitted to Aflimart Digital Market. Our team is reaching out regarding your submission ahead of our launch.\n\nBest regards,\nAflimart Digital Market Team`
            );
            return email || wa ? (
              <div className="mb-6 rounded-xl border border-[#1E3A8A]/25 bg-[#1E3A8A]/5 p-4" data-testid={`${testId}-followup`}>
                <p className="font-sans text-[10px] tracking-[0.25em] text-[#1E3A8A]/70">FOLLOW UP WITH THIS APPLICANT</p>
                <div className="mt-3 flex flex-wrap gap-3">
                  {email && (
                    <a
                      href={`https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email)}&su=${subject}&body=${body}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`${testId}-followup-email`}
                      className="btn-gold !px-5 !py-2 !text-xs"
                    >
                      <Mail size={13} /> Email
                    </a>
                  )}
                  {wa && (
                    <a
                      href={`https://wa.me/${wa}?text=${body}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`${testId}-followup-whatsapp`}
                      className="btn-ghost !px-5 !py-2 !text-xs"
                    >
                      <MessageCircle size={13} /> WhatsApp
                    </a>
                  )}
                </div>
                <p className="mt-3 text-xs leading-relaxed text-[#94A3B8]">
                  Email opens a ready-to-send Gmail draft in a new tab; WhatsApp opens a chat with a neutral follow-up draft — applicants are not marked approved by contacting them.
                </p>
              </div>
            ) : null;
          })()}
          <dl className="space-y-4 text-sm" data-testid={`${testId}-detail-fields`}>
            {data.headers.map((h) => (
              <div key={h}>
                <dt className="font-sans text-[10px] tracking-[0.2em] text-[#94A3B8]">{h.toUpperCase()}</dt>
                <dd className="mt-1 whitespace-pre-wrap break-words leading-relaxed text-[#475569]">{selected[h] || "—"}</dd>
              </div>
            ))}
          </dl>
        </DetailModal>
      )}
    </div>
  );
};
